import { AnalyticsMetricId, MetricFormat } from 'services/user'
import { METRIC_BY_METRIC_ID } from '../components/key-metrics-data'

// Value + growth formatting for the Analytics Report, matching the ticket's
// per-metric format table (§3.7). Currency symbol follows the website config.

// Intl formatters are costly to construct and cheap to use; a chart panel
// formats hundreds of numbers per render, so each distinct configuration is
// built once and reused.
const formatters = new Map<string, Intl.NumberFormat>()
const cached = (key: string, make: () => Intl.NumberFormat) => {
	let f = formatters.get(key)
	if (!f) {
		f = make()
		formatters.set(key, f)
	}
	return f
}

const nf = (min: number, max: number) =>
	cached(
		`n|${min}|${max}`,
		() =>
			new Intl.NumberFormat('en-US', {
				minimumFractionDigits: min,
				maximumFractionDigits: max
			})
	)

// Duration input is milliseconds (avg behavior_time_spent). e.g. 134000 → "2m 14s".
const formatDuration = (ms: number): string => {
	const totalSec = Math.round(ms / 1000)
	const h = Math.floor(totalSec / 3600)
	const m = Math.floor((totalSec % 3600) / 60)
	const s = totalSec % 60
	if (h > 0) return `${h}h ${m}m`
	if (m > 0) return `${m}m ${s}s`
	return `${s}s`
}

const currencyFmt = (currency: string, decimals: number) =>
	cached(
		`c|${currency || 'USD'}|${decimals}`,
		() =>
			new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: currency || 'USD',
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals
			})
	)

export const formatMetricValue = (
	value: number | null | undefined,
	format: MetricFormat,
	currency = 'USD'
): string => {
	if (value === null || value === undefined) return '–' // en-dash: unavailable
	switch (format) {
		case 'integer':
			return nf(0, 0).format(Math.round(value))
		case 'currency0':
			return currencyFmt(currency, 0).format(Math.round(value))
		case 'currency2':
			return currencyFmt(currency, 2).format(value)
		case 'percent1':
			return `${nf(1, 1).format(value)}%`
		case 'decimal1':
			return nf(1, 1).format(value)
		case 'duration':
			return formatDuration(value)
		default:
			return String(value)
	}
}

export interface GrowthPillProps {
	type: 'arrow'
	value: string
	trend: 'positive' | 'negative' | 'neutral'
	direction: 'up' | 'down'
}

// Growth vs previous period. Always purple/neutral (§3.3) except Bad traffic
// rate, which keeps a semantic color: decreasing → green, increasing → red.
export const growthPillProps = (
	metricId: AnalyticsMetricId,
	value: number | null | undefined,
	prevValue: number | null | undefined
): GrowthPillProps => {
	if (value == null || prevValue == null || prevValue === 0) {
		return { type: 'arrow', value: '0.0%', trend: 'neutral', direction: 'up' }
	}
	const pct = ((value - prevValue) / Math.abs(prevValue)) * 100
	const direction: 'up' | 'down' = pct >= 0 ? 'up' : 'down'
	const semantic = metricId === 'bad_traffic_rate'
	const trend: GrowthPillProps['trend'] = semantic
		? direction === 'down'
			? 'positive'
			: 'negative'
		: 'neutral'
	return {
		type: 'arrow',
		value: `${Math.abs(pct).toFixed(1)}%`,
		trend,
		direction
	}
}

// ── Chart formatting (DEV-2432 §2.2 / §5.2 / §5.3) ──────────────────────────
// ONE formatter per chart panel, derived from the metric's registry format, so
// the axis ticks, the bar values, the legend values and the tooltip all print in
// the metric's own unit: `$10,000` for a currency, `12.5%` for a rate, `1,284`
// for a count. Ticks drop the decimals a value keeps (`$10,000`, not `$10,000.00`).
export interface ChartFormat {
	fmtValue: (n: number) => string
	fmtTick: (n: number) => string
	/** True for a rate metric: values are already shares, so the axis caps at 100. */
	isPct: boolean
	/** The smallest difference two tick labels can show — ticks print whole
	 *  units for counts and money and one decimal for rates. The axis never
	 *  steps finer than this, so $1 · $1 · $1 · $0 · $0 · $0 can't happen. */
	tickStep: number
}

export const chartFormatFor = (
	format: MetricFormat,
	currency = 'USD'
): ChartFormat => {
	const isPct = format === 'percent1'
	const isMoney = format === 'currency0' || format === 'currency2'
	return {
		fmtValue: (n) => formatMetricValue(n, format, currency),
		fmtTick: isPct
			? (v) => `${+v.toFixed(1)}%`
			: isMoney
			? (v) => formatMetricValue(Math.round(v), 'currency0', currency)
			: (v) => formatMetricValue(Math.round(v), 'integer'),
		isPct,
		tickStep: isPct ? 0.1 : 1
	}
}

/**
 * The chart formatter for a metric id: the server registry's format when the
 * caller has it (`useAnalyticsMeta`), else the key-metrics catalogue's — the
 * same table the cards and the render route already read.
 */
export const chartFormatForMetric = (
	metricId: AnalyticsMetricId | undefined,
	currency = 'USD',
	registryFormat?: MetricFormat
): ChartFormat =>
	chartFormatFor(
		registryFormat ??
			(metricId ? METRIC_BY_METRIC_ID[metricId]?.format : undefined) ??
			'integer',
		currency
	)
